import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react'
import { useGetSimulationForReviewQuery } from '../app/services/simulationCreatorApi'
import { ReviewerContent } from '../features/simulation-review'
import Sidebar from '../features/sidebar/components/sidebar'
import HeaderSection from '../components/header-section'

export default function SimulationReview() {
  const { id } = useParams<{ id: string }>()
  const { data: simulation, isLoading, error } = useGetSimulationForReviewQuery(id!, {
    skip: !id,
  })

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col overflow-hidden">
        <HeaderSection />

        {/* Page header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/simulations"
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Simulations
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {simulation?.name || 'Simulation Review'}
                </h1>
                {simulation && (
                  <p className="text-sm text-gray-500">
                    Review simulation content and measure assignments
                  </p>
                )}
              </div>
            </div>

            {simulation && (
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    simulation.status === 'PUBLISHED'
                      ? 'bg-green-100 text-green-700'
                      : simulation.status === 'COMPLETED'
                      ? 'bg-blue-100 text-blue-700'
                      : simulation.status === 'IN_PROGRESS'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {simulation.status}
                </span>
                <Link
                  to={`/simulation-creator/${id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md"
                >
                  <ExternalLink className="w-4 h-4" />
                  Edit in Creator
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-gray-500">Loading simulation...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-red-600 font-medium mb-2">Failed to load simulation</p>
                <p className="text-gray-500 text-sm">
                  {'status' in error
                    ? `Error ${error.status}`
                    : 'An unexpected error occurred'}
                </p>
                <Link
                  to="/simulations"
                  className="inline-flex items-center gap-1 mt-4 text-blue-600 hover:text-blue-700"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Simulations
                </Link>
              </div>
            </div>
          )}

          {simulation && <ReviewerContent simulation={simulation} />}
        </div>
      </div>
    </div>
  )
}
